import {useEffect, useRef} from 'react';

export interface YouTubeEmbedProps {
  /** YouTube video ID or full YouTube URL */
  video: string;
  /** Width of the video player in pixels */
  width: number;
  /** Height of the video player in pixels */
  height: number;
  /** Whether to autoplay the video (default: false) */
  autoplay?: boolean;
  /** Language code for the player interface (default: 'en') */
  lang?: string;
  /** Whether to show captions by default (default: false) */
  showCaptions?: boolean;
  /** Whether to auto-focus the iframe when mounted (default: false) */
  autoFocus?: boolean;
}

/** A YouTube video ID is always exactly 11 base64url characters. */
const VIDEO_ID_PATTERN = /^[a-zA-Z0-9_-]{11}$/;

/**
 * Extracts a YouTube video ID from a video ID string or full URL.
 *
 * Supports formats:
 * - Plain video ID: "dQw4w9WgXcQ"
 * - Standard URL: "https://www.youtube.com/watch?v=dQw4w9WgXcQ"
 * - Short URL: "https://youtu.be/dQw4w9WgXcQ"
 * - Embed URL: "https://www.youtube.com/embed/dQw4w9WgXcQ"
 * - Schemeless variants of the above (e.g. "youtube.com/watch?v=dQw4w9WgXcQ")
 * - `v=` typo'd into the path instead of the query string (e.g.
 *   "youtube.com/watch/v=dQw4w9WgXcQ")
 *
 * Returns the input unchanged if no id can be extracted.
 */
export function extractVideoId(video: string): string {
  // If it doesn't look like a URL, assume it's already a video ID
  if (!video.includes('/') && !video.includes('.')) {
    return video;
  }

  try {
    // `new URL` requires a scheme; add one so schemeless input (a common
    // paste/typo) still parses instead of throwing.
    const url = new URL(video.includes('://') ? video : `https://${video}`);

    // Handle youtu.be short URLs
    if (url.hostname === 'youtu.be') {
      return url.pathname.slice(1);
    }

    // Handle youtube.com URLs
    if (url.hostname.includes('youtube.com')) {
      // Embed URLs: /embed/VIDEO_ID
      if (url.pathname.startsWith('/embed/')) {
        return url.pathname.slice(7);
      }
      // Standard URLs: ?v=VIDEO_ID
      const vParam = url.searchParams.get('v');
      if (vParam) {
        return vParam;
      }
    }
  } catch {
    // Fall through to the regex fallback below.
  }

  // Fallback for `v=` that ended up in the path instead of the query string
  // (e.g. "/watch/v=ID" instead of "/watch?v=ID"), which `URLSearchParams`
  // above won't find.
  const pathVMatch = video.match(/[?&/]v=([a-zA-Z0-9_-]{11})/);
  if (pathVMatch) {
    return pathVMatch[1];
  }

  return video;
}

/**
 * Returns true if `url` is suitable for {@link YouTubeEmbed}: a YouTube watch
 * / short / embed URL that a video ID can actually be extracted from, or a
 * plain 11-character video id.
 */
export function isYouTubeUrl(url: string): boolean {
  if (!url.includes('youtube.com') && !url.includes('youtu.be')) {
    // Plain video ID (11 base64url chars) — no dots or slashes.
    return !url.includes('/') && !url.includes('.') && VIDEO_ID_PATTERN.test(url);
  }
  return VIDEO_ID_PATTERN.test(extractVideoId(url));
}

/**
 * Checks if the current page is cross-origin isolated (has COEP headers).
 * When isolated, we can't embed YouTube directly and need a proxy.
 */
function isCrossOriginIsolated(): boolean {
  // Use the standard API to check cross-origin isolation status
  return window.crossOriginIsolated === true;
}

/**
 * Builds a YouTube embed URL with the given options.
 * Uses direct YouTube embed when not cross-origin isolated,
 * falls back to proxy when COEP headers are present.
 */
function buildEmbedUrl(
  videoId: string,
  options: {autoplay: boolean; lang: string; showCaptions: boolean}
): string {
  // If page is cross-origin isolated, use the proxy
  if (isCrossOriginIsolated()) {
    return `https://cip-embeds.web.app/youtube.html?v=${videoId}`;
  }

  // Otherwise, embed YouTube directly
  const params = new URLSearchParams({
    autoplay: options.autoplay ? '1' : '0',
    controls: '1',
    modestbranding: '1',
    rel: '0',
    hl: options.lang,
    cc_load_policy: options.showCaptions ? '1' : '0',
    enablejsapi: '0',
  });
  return `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
}

/**
 * A YouTube video embed component that works with Cross-Origin Isolation (COEP).
 *
 * Uses a direct iframe with the `credentialless` attribute instead of the YouTube
 * IFrame API, which is incompatible with COEP-restricted environments.
 *
 * Note: Because this doesn't use the YouTube IFrame API, player events (like
 * video end detection) are not available.
 */
export function YouTubeEmbed({
  video,
  width,
  height,
  autoplay = false,
  lang = 'en',
  showCaptions = false,
  autoFocus = false,
}: YouTubeEmbedProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const videoId = extractVideoId(video);
  const embedUrl = buildEmbedUrl(videoId, {autoplay, lang, showCaptions});

  useEffect(() => {
    if (autoFocus && iframeRef.current) {
      iframeRef.current.focus();
    }
  }, [autoFocus]);

  if (width <= 0 || height <= 0) {
    return null;
  }

  return (
    <iframe
      ref={iframeRef}
      width={width}
      height={height}
      src={embedUrl}
      title="YouTube video player"
      style={{border: 'none'}}
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
    />
  );
}
